import React from 'react';
import SchemaModal from '../ui/SchemaModal';
import Tabs from '../ui/Tabs';
import ProgressBar from '../ui/ProgressBar';
import ButtonSpinner from '../ui/ButtonSpinner';
import Input from '../ui/Input';
import Textarea from '../ui/Textarea';
import { Plus, Download, Info, Check } from 'lucide-react';
import { ReactComponent as ArchidektLogo } from '../assets/archidekt_logo.svg';
import './NewDeckModal.css';

/**
 * Getabter „Neues Deck"-Modal: Erstellen (Name + optionale Kartenliste) | Archidekt-Import
 * (Einzel-Import + einmaliger Profil-Massenimport mit Fortschritt). Auf ui/-Master aufgebaut.
 */
export default function NewDeckModal(props) {
  const {
    open, onOpenChange, activeTab, setActiveTab,
    loading, error,
    newDeckName, setNewDeckName, newDeckScryfallIds, setNewDeckScryfallIds, handleCreateDeck,
    archidektInput, setArchidektInput, importLoading, importError, handleArchidektImport,
    bulkUsername, setBulkUsername, bulkStep, bulkDecks, bulkSelectedIds,
    bulkLoading, bulkError, bulkStatus, bulkImportDone,
    handleBulkFetchDecks, handleBulkStartImport, handleBulkToggleDeck,
    handleBulkSelectAll, handleBulkDeselectAll, handleBulkBack, handleCloseBulkModal,
  } = props;

  const isImporting = bulkStep === 'importing';

  return (
    <SchemaModal
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      dismissOnBackdrop={false}
      title="Neues Deck erstellen oder importieren"
    >
      <div className="new-deck-modal">
      <Tabs value={activeTab} onValueChange={(v) => { if (!isImporting) setActiveTab(v); }}>
        <Tabs.List>
          <Tabs.Trigger value="create" icon={Plus} disabled={isImporting}>Neues Deck</Tabs.Trigger>
          <Tabs.Trigger value="archidekt" icon={ArchidektLogo} disabled={isImporting}>Archidekt</Tabs.Trigger>
        </Tabs.List>

        <Tabs.Panel value="create">
          <div className="new-deck-modal__field">
            <Input type="text" placeholder="Deckname" value={newDeckName} onChange={(e) => setNewDeckName(e.target.value)} autoFocus />
          </div>
          <div className="new-deck-modal__field">
            <Textarea placeholder={"Sol Ring\n1 Lightning Bolt\n3x Forest"} value={newDeckScryfallIds} onChange={(e) => setNewDeckScryfallIds(e.target.value)} rows={8} />
            <p className="new-deck-modal__hint">
              Eine Karte pro Zeile — Kartenname (exakt) oder Scryfall-ID, optional mit Menge davor
              (z. B. „3 Forest", „2x Lightning Bolt"). Ohne Menge = 1.
            </p>
          </div>
          <div className="new-deck-modal__actions">
            <button type="button" className="btn-secondary" onClick={() => onOpenChange(false)}>Abbrechen</button>
            <button type="button" className="btn-primary" onClick={handleCreateDeck} disabled={loading}>
              {loading ? <ButtonSpinner size={14} /> : <Plus size={14} aria-hidden />} {loading ? 'Erstelle…' : 'Deck erstellen'}
            </button>
          </div>
          {error && <p className="new-deck-modal__error">Fehler: {error}</p>}
        </Tabs.Panel>

        <Tabs.Panel value="archidekt">
          {bulkStep === 'input' && (
            <>
              <h3 className="new-deck-modal__subhead">Deck importieren</h3>
              <div className="new-deck-modal__infobox">
                <Info size={16} aria-hidden />
                <span>Nur öffentlich freigegebene Decks können importiert werden.</span>
              </div>
              <Input type="text" placeholder="Archidekt URL oder Deck-ID" value={archidektInput} onChange={(e) => setArchidektInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !importLoading) handleArchidektImport(); }} autoFocus />
              <p className="new-deck-modal__hint">z.B. https://archidekt.com/decks/20188690/</p>
              <div className="new-deck-modal__actions">
                <button type="button" className="btn-primary" onClick={handleArchidektImport} disabled={importLoading}>
                  {importLoading ? <ButtonSpinner size={14} /> : <Download size={14} aria-hidden />} {importLoading ? 'Importiere…' : 'Deck importieren'}
                </button>
              </div>
              {importError && <p className="new-deck-modal__error">Fehler: {importError}</p>}

              {!bulkImportDone && (
                <>
                  <hr className="new-deck-modal__divider" />
                  <h3 className="new-deck-modal__subhead">Profil importieren</h3>
                  <div className="new-deck-modal__infobox">
                    <Info size={16} aria-hidden />
                    <span>Die öffentlichen Decks eines Archidekt-Benutzers werden als virtuelle Decks importiert. Diese Funktion kann nur einmal verwendet werden.</span>
                  </div>
                  <Input type="text" placeholder="Archidekt Benutzername" value={bulkUsername} onChange={(e) => setBulkUsername(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !bulkLoading) handleBulkFetchDecks(); }} />
                  <div className="new-deck-modal__actions">
                    <button type="button" className="btn-primary" onClick={handleBulkFetchDecks} disabled={bulkLoading}>
                      {bulkLoading && <ButtonSpinner size={14} />} {bulkLoading ? 'Lade Decks…' : 'Profil importieren'}
                    </button>
                  </div>
                  {bulkError && <p className="new-deck-modal__error">Fehler: {bulkError}</p>}
                </>
              )}
            </>
          )}

          {bulkStep === 'select' && (
            <>
              <p className="new-deck-modal__hint">{bulkDecks.length} Decks gefunden. Wähle die Decks aus, die importiert werden sollen:</p>
              <div className="new-deck-modal__bulk-actions">
                <button type="button" className="btn-secondary" onClick={handleBulkSelectAll}>Alle auswählen</button>
                <button type="button" className="btn-secondary" onClick={handleBulkDeselectAll}>Alle abwählen</button>
              </div>
              <div className="new-deck-modal__bulk-list">
                {bulkDecks.map((deck) => (
                  <label key={deck.id} className="new-deck-modal__bulk-item">
                    <input type="checkbox" checked={bulkSelectedIds.has(deck.id)} onChange={() => handleBulkToggleDeck(deck.id)} />
                    <span className="new-deck-modal__bulk-name">{deck.name}</span>
                    {deck.deckFormat && <span className="new-deck-modal__bulk-format">{deck.deckFormat}</span>}
                  </label>
                ))}
              </div>
              <p className="new-deck-modal__hint">{bulkSelectedIds.size} von {bulkDecks.length} ausgewählt</p>
              <div className="new-deck-modal__actions">
                <button type="button" className="btn-secondary" onClick={handleBulkBack}>Zurück</button>
                <button type="button" className="btn-primary" onClick={handleBulkStartImport} disabled={bulkLoading || bulkSelectedIds.size === 0}>
                  {bulkLoading ? <ButtonSpinner size={14} /> : <Download size={14} aria-hidden />} {bulkLoading ? 'Starte…' : `${bulkSelectedIds.size} Decks importieren`}
                </button>
              </div>
            </>
          )}

          {bulkStep === 'importing' && (
            <div className="new-deck-modal__center">
              <p>Import läuft… Bitte nicht schließen.</p>
              {bulkStatus && (
                <>
                  <ProgressBar value={bulkStatus.progress ?? 0} max={bulkStatus.total ?? 0} label="Import-Fortschritt" />
                  <p className="new-deck-modal__hint">{bulkStatus.progress} / {bulkStatus.total} Decks</p>
                  {bulkStatus.currentDeckName && <p className="new-deck-modal__hint">Aktuell: {bulkStatus.currentDeckName}</p>}
                </>
              )}
              {bulkStatus?.status === 'error' && <p className="new-deck-modal__error">Fehler: {bulkStatus.error || 'Unbekannter Fehler'}</p>}
            </div>
          )}

          {bulkStep === 'done' && (
            <div className="new-deck-modal__center">
              <Check size={32} className="new-deck-modal__done-icon" aria-hidden />
              <p>Import abgeschlossen!</p>
              {bulkStatus?.results && (
                <p className="new-deck-modal__hint">{bulkStatus.results.filter((r) => r.deckId).length} Decks erfolgreich importiert.</p>
              )}
              <div className="new-deck-modal__actions new-deck-modal__actions--center">
                <button type="button" className="btn-primary" onClick={handleCloseBulkModal}>Schließen</button>
              </div>
            </div>
          )}

          {bulkStep !== 'input' && bulkError && <p className="new-deck-modal__error">Fehler: {bulkError}</p>}
        </Tabs.Panel>
      </Tabs>
      </div>
    </SchemaModal>
  );
}
